import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Button, Spinner, Input, Logo, ScreenLoader } from '../../components/design-system';
import { PasswordRules } from '../../components/auth/PasswordRules';
import { friendlyPasswordError, passwordProblem } from '../../lib/passwordPolicy';

/**
 * ResetPassword — where a password-recovery link lands (via AuthCallback,
 * once it recognises `type=recovery` and routes here) with a session
 * already established by Supabase's verifyOtp. Sets a new password on that
 * session with updateUser(), rather than requiring the old one.
 *
 * Deliberately doesn't gate on arriving via the recovery flow specifically —
 * any authenticated session can use this as a general "change my password"
 * screen — but it does require SOME session, since updateUser needs one.
 * An expired/already-used recovery link lands here with none, so that's
 * treated as the same "link no longer valid" case either way.
 */
export function ResetPassword() {
  const navigate = useNavigate();
  const [checkingSession, setCheckingSession] = useState(true);
  const [hasSession, setHasSession] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setHasSession(!!data.session);
      setCheckingSession(false);
    });
    return () => { cancelled = true; };
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    const problem = passwordProblem(password);
    if (problem) {
      setError(problem);
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      setDone(true);
    } catch (err: unknown) {
      setError(friendlyPasswordError(err) ?? (err instanceof Error ? err.message : 'Something went wrong. Please try again.'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-dvh w-full" style={{ background: 'rgb(var(--violet))' }} data-kb-backdrop="violet">
      <div className="min-h-dvh flex flex-col items-center pt-safe-or-12 pb-safe-or-6 px-safe-or-6">
        <div className="w-full max-w-sm">
          <div className="flex flex-col items-center text-center">
            <Logo variant="full" height={22} style={{ color: 'rgb(var(--cream-bright))' }} />
            <h1
              className="font-serif mt-6"
              style={{
                fontSize: 'var(--fs-page-title)',
                lineHeight: 'var(--lh-page-title)',
                letterSpacing: '-0.03em',
                color: 'rgb(var(--cream-bright))',
              }}
            >
              Set a new password
            </h1>
          </div>

          <div style={{ height: 32 }} />

          {checkingSession ? (
            <div className="flex justify-center py-6"><ScreenLoader /></div>
          ) : !hasSession ? (
            <div className="text-center">
              <p className="text-sm" style={{ color: 'rgba(255, 252, 219, .7)' }}>
                This link has expired or has already been used. Request a new one to reset your password.
              </p>
              <div style={{ height: 24 }} />
              <Link
                to="/forgot-password"
                className="text-sm underline underline-offset-2 font-medium sk-hit"
                style={{ color: 'rgb(var(--cream-bright))' }}
              >
                Request a new link
              </Link>
            </div>
          ) : done ? (
            <div className="text-center">
              <h2 className="font-serif text-2xl mb-2" style={{ color: 'rgb(var(--cream-bright))' }}>Password updated</h2>
              <p className="text-sm mb-6" style={{ color: 'rgba(255, 252, 219, .7)' }}>
                Your password has been changed.
              </p>
              <Button
                type="button"
                size="xs"
                tone="inverted"
                fullWidth
                onClick={() => navigate('/', { replace: true })}
              >
                Continue
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <Input
                id="password"
                type="password"
                label="New password"
                tone="inverted"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="new-password"
                aria-describedby="password-rules"
              />
              <PasswordRules password={password} tone="inverted" />
              <Input
                id="confirmPassword"
                type="password"
                label="Confirm new password"
                tone="inverted"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="new-password"
              />
              {error && <p className="text-sm text-crimson">{error}</p>}
              <Button
                type="submit"
                size="xs"
                tone="inverted"
                fullWidth
                disabled={loading}
                className="mt-2"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Spinner size="sm" />
                    Saving…
                  </span>
                ) : (
                  'Save new password'
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
